import {
  deriveClientAddress,
  deriveClientPackageName,
  deriveServiceTypeDescription,
  deriveServiceTypeSelectorParams,
} from '~appointments/lib/derive';
import { initialiseUserOption } from '~appointments/lib/initialise';
import { onChangeClient, onChangePackageHandler, onChangeServiceType } from '~appointments/lib/onchange';
import { validateStartEndTimes } from '~appointments/lib/validate';

import { Step } from '~components/wizard-form/props';
import { FormInputCareWorkers, FormInputDate, FormInputStartEndTimes } from '~appointments/components';
import WizardInputPackageSelector from '~weekly-planner/components/wizard/WizardInputPackageSelector';
import WizardReadonlyQualifications from '~weekly-planner/components/wizard/WizardReadonlyQualifications';

import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';

import { selectCareQualificationLevels } from '~appointments/selectors/careQualificationLevels';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectClientPackages } from '~main/selectors/clients/clientPackages';
import { selectLookupClients } from '~main/selectors/clients/clients';

import AddAppointmentSummary from './AddAppointmentSummary';

const addAppointmentWizardFields: Step[] = [
  {
    step: 1,
    title: 'Quick Add Appointment',
    fields: {
      id: {
        type: 'hidden',
      },
      date: {
        type: 'custom',
        component: FormInputDate,
        caption: 'Appointment Date',
        required: true,
        colSize: 6,
        order: 100,
      },

      status: {
        type: 'readonly',
        field: 'status.name',
        order: 10,
      },
      client_name: {
        type: 'select',
        field: 'client_id',
        caption: 'Client / Facility',
        deriveDescription: deriveClientAddress,
        onChange: onChangeClient,
        className: 'text-left',
        required: true,
        colSize: 6,
        order: 200,
        selectorOptions: selectLookupClients,
      },
      package_fixed_id: {
        type: 'custom',
        component: WizardInputPackageSelector,
        dependencies: ['package_fixed_id', 'client_id', 'date'],
        onChange: onChangePackageHandler,
        selectorOptions: selectClientPackages,
        required: true,
        colSize: 6,
        order: 300,
      },

      client_package_name: {
        type: 'readonly',
        caption: 'Default Package',
        deriveValue: deriveClientPackageName,
        className: 'text-left',
        order: 400,
      },
      start_end_time: {
        type: 'custom',
        caption: 'Start/End Times',
        colSize: 6,
        component: FormInputStartEndTimes,
        dependencies: ['start_time', 'end_time', 'ends_next_day'],
        validate: validateStartEndTimes,
        order: 500,
      },
      show_notes: {
        type: 'switch',
        caption: 'Add Notes',
        colSize: 6,
        order: 600,
        isAttribute: false,
        dataCy: 'quick-add-show-notes-switch',
      },
      notes: {
        type: 'textarea',
        caption: 'Notes',
        placeholder: 'Instructions for care workers, specific to this appointment',
        show: (values: any) => !!values.show_notes,
        colSize: 12,
        order: 700,
        dataCy: 'quick-add-notes-textarea',
      },
    },
  },
  {
    step: 2,
    title: 'Worker & Qualifications',
    fields: {
      service_type_id: {
        type: 'select-lazy',
        caption: 'Service Type',
        initialOption: { id: 'service_type', name: '' },
        selectorOptions: selectAppointmentServiceTypes,
        selectorActions: getServiceTypes,
        selectorParams: deriveServiceTypeSelectorParams,
        onChange: onChangeServiceType,
        deriveWarning: deriveServiceTypeDescription,
        order: 100,
        colSize: 6,
        required: true,
      },
      qualification_level: {
        type: 'custom',
        caption: 'Qualifications Required',
        component: WizardReadonlyQualifications,
        dependencies: ['service_type_id', 'qualification_level'],
        selectorOptions: selectCareQualificationLevels,
        order: 200,
      },
      user_id: {
        type: 'custom',
        caption: 'Care Worker',
        component: FormInputCareWorkers,
        dependencies: ['client_id', 'user_id', 'date', 'id', 'repeat'],
        initialiseInitialValue: initialiseUserOption,
        colSize: 6,
        order: 300,
      },
    },
  },
  {
    step: 3,
    title: 'Confirm',
    fields: {
      confirm: {
        type: 'custom',
        component: AddAppointmentSummary,
        colSize: 12,
        dependencies: [
          'date',
          'client_id',
          'package_fixed_id',
          'start_time',
          'end_time',
          'ends_next_day',
          'service_type_id',
          'user_id',
          'notes',
        ],
        order: 10,
      },
    },
  },
];

export default addAppointmentWizardFields;
